import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { withRouter } from '../../common/withRouter';
import { Notification, Message } from '../../web.common';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';
import { fmtShort, fmtMoney, StatusBadge, tableWrap, table, headRow, bodyRow, cell, muted } from './playGameFormat';

// PLAY "Suggest a game" overview: every 1v1 game players have posted, newest
// first, with how far each one got (sides paid, invites, money in/out).
// Status filtering is server-side; the text search is client-side over the
// loaded rows (code, creator, facility, court).
const STATUSES = ['Open', 'Confirmed', 'Played', 'Expired', 'Cancelled', 'Draft'];

const PlayGamesInternal = (props) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [notification, setNotification] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await props.api.playGames.list({ status: status || undefined, take: 500 });
      setGames(Array.isArray(data?.games) ? data.games : []);
    } catch (e) {
      setNotification(new Message('error', `Failed to load: ${e?.message || 'unknown'}`));
    } finally {
      setLoading(false);
    }
  }, [props.api, status]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return games;
    return games.filter((g) =>
      [g.code, g.creatorName, g.facilityName, g.courtName, g.sport]
        .some((v) => v && String(v).toLowerCase().includes(q)));
  }, [games, search]);

  const counts = useMemo(() => {
    const c = {};
    games.forEach((g) => { c[g.status] = (c[g.status] || 0) + 1; });
    return c;
  }, [games]);

  return (
    <>
      <Notification newMessage={notification} />
      <PageTop documentTitle='Play games' documentDescription='PLAY games posted by players' mainHeadingContent='Play games' />
      <div style={{ padding: 16, color: '#444', maxWidth: 1300 }}>
        <div style={{ marginBottom: 8 }}>
          <Link to='/play' style={{ color: '#666', fontSize: 13 }}>← Back to Play</Link>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: 6 }}>
            <option value=''>All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            type='search'
            placeholder='Search code, player, facility…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: 6, minWidth: 260 }}
          />
          <button type='button' onClick={load} disabled={loading} style={{ padding: '6px 12px' }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {!status && !loading && (
            <span style={muted}>
              {games.length} games
              {STATUSES.filter((s) => counts[s]).map((s) => ` · ${counts[s]} ${s.toLowerCase()}`).join('')}
            </span>
          )}
        </div>

        {loading && games.length === 0 ? (
          <p>Loading…</p>
        ) : visible.length === 0 ? (
          <p>No games.</p>
        ) : (
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr style={headRow}>
                  <th style={cell}>Game</th>
                  <th style={cell}>Status</th>
                  <th style={cell}>Created by</th>
                  <th style={cell}>Where</th>
                  <th style={cell}>Starts</th>
                  <th style={cell}>Sides paid</th>
                  <th style={cell}>Invites</th>
                  <th style={cell}>Price / share</th>
                  <th style={cell}>Charged / refunded</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((g) => (
                  <tr key={g.code} style={bodyRow}>
                    <td style={cell}>
                      <Link to={`/play/games/${g.code}`} style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{g.code}</Link>
                      <div style={{ ...muted, whiteSpace: 'nowrap' }}>{g.sport} · {g.format}{g.gameType === 'Ranked' ? ' · Ranked' : ''}</div>
                      <div style={{ ...muted, whiteSpace: 'nowrap' }}>created {fmtShort(g.createdAtUtc)}</div>
                    </td>
                    <td style={cell}>
                      <StatusBadge status={g.status} />
                      {g.status === 'Open' && <div style={muted}>expires {fmtShort(g.expiresAtUtc)}</div>}
                      {g.confirmedAtUtc && <div style={muted}>confirmed {fmtShort(g.confirmedAtUtc)}</div>}
                      {g.cancelledAtUtc && <div style={muted}>cancelled {fmtShort(g.cancelledAtUtc)}</div>}
                    </td>
                    <td style={cell}>
                      {g.creatorUserId
                        ? <Link to={`/user/${g.creatorUserId}`}>{g.creatorName || g.creatorUserId}</Link>
                        : '—'}
                    </td>
                    <td style={cell}>
                      {g.facilityName || '—'}
                      {g.courtName && <div style={muted}>{g.courtName}</div>}
                    </td>
                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{fmtShort(g.startsAt)}</td>
                    <td style={cell}>{g.sidesPaid} / {g.sidesTotal}</td>
                    <td style={cell}>
                      {g.invitesTotal
                        ? <>{g.invitesTotal}<div style={muted}>{g.invitesViewed} viewed · {g.invitesSkipped} skipped</div></>
                        : <span style={{ color: '#bbb' }}>0</span>}
                    </td>
                    <td style={cell}>
                      <span style={{ whiteSpace: 'nowrap' }}>{fmtMoney(g.totalPriceMinor, g.currencyIsoCode)}</span>
                      <div style={{ ...muted, whiteSpace: 'nowrap' }}>{fmtMoney(g.shareAmountMinor, g.currencyIsoCode)} each</div>
                    </td>
                    <td style={cell}>
                      <span style={{ whiteSpace: 'nowrap' }}>{fmtMoney(g.chargedMinor, g.currencyIsoCode)}</span>
                      {g.refundedMinor > 0 && <div style={{ ...muted, color: '#8a2b2b', whiteSpace: 'nowrap' }}>−{fmtMoney(g.refundedMinor, g.currencyIsoCode)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

PlayGamesInternal.propTypes = { api: PropTypes.object.isRequired };

export default connect(() => ({}))(withRouter(withApi(PlayGamesInternal)));
